'use client';

import clsx from 'clsx';
import { useMemo, useRef } from 'react';
import { Select, useSelectStore } from '@ariakit/react';
import { FieldValues, Path, useController } from 'react-hook-form';
import { DallasOptionItemProps } from '@ui/Dallas/Forms/DallasOptionItem/DallasOptionItem';
import { AbstractControl } from '../abstract-control';
import DallasFlex from '../../Stack/DallasFlex';
import DallasSelectItem from '~/app/_ui/Dallas/Forms/DallasSelectField/DallasSelectItem';
import DallasSelectPopover from '~/app/_ui/Dallas/Forms/DallasSelectField/DallasSelectPopover';
import DallasSvgIconRadioButtonCheckedOutlined from '~/app/_ui/Dallas/Icons/DallasSvgIconRadioButtonCheckedOutlined';
import DallasSvgIconRadioButtonUncheckedOutlined from '~/app/_ui/Dallas/Icons/DallasSvgIconRadioButtonUncheckedOutlined';
import DallasFormField from '../DallasFormField/DallasFormField';
import DallasFormControlIconButton from '../DallasFormControlIconButton/DallasFormControlIconButton';
import DallasSvgIconCloseOutlined from '../../Icons/DallasSvgIconCloseOutlined';
import DallasSvgIconExpandMoreOutlined from '../../Icons/DallasSvgIconExpandMoreOutlined';
import DallasSvgIconExpandLessOutlined from '../../Icons/DallasSvgIconExpandLessOutlined';

import styles from './DallasSelectField.module.scss';
import formFieldStyles from '../DallasFormField/DallasFormField.module.scss';

interface Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends AbstractControl<TName, string> {
  options: DallasOptionItemProps<string>[];
  addOnStart?: React.ReactElement;
  footnoteStart?: React.ReactElement;
  // Makes the popover a render as modal.
  withModalPopover?: boolean;
}

export default function DallasSelectField<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  className,
  errorPlacement,
  label,
  name,
  options,
  disabled,
  required,
  addOnStart,
  placeholder = 'Select an option',
  footnoteStart,
  withModalPopover,
  onChange: onValueChange,
}: Props<TFieldValues, TName>) {
  const controlContainerRef = useRef<HTMLDivElement>(null);
  const {
    fieldState: { error },
    field: { ref, value, onChange, onBlur },
  } = useController<TFieldValues, TName>({
    name,
    rules: {
      required: required
        ? typeof required === 'string'
          ? required
          : 'Required field'
        : false,
    },
  });
  const labels = useMemo(
    () =>
      options.reduce(
        (labels, { value, label }) => ((labels[value] = label), labels),
        {} as Partial<Record<string, string>>,
      ),
    [options],
  );

  function renderValue(value: string) {
    return labels[value] ?? placeholder;
  }

  function setValue(value: string) {
    onChange(value);
    onValueChange?.(value);
  }

  const selectStore = useSelectStore({
    value: value ?? '',
    setValue,
  });

  const isOpen = selectStore.useState('open');

  const addOnEnd =
    !disabled && !!value ? (
      <DallasFormControlIconButton
        className={styles.addOnEnd}
        icon={<DallasSvgIconCloseOutlined />}
        aria-label={'Clear value'}
        onClick={() => setValue('')}
        rounded
      />
    ) : (
      <DallasFormControlIconButton
        className={styles.addOnEnd}
        tabIndex={-1}
        icon={
          isOpen ? (
            <DallasSvgIconExpandLessOutlined />
          ) : (
            <DallasSvgIconExpandMoreOutlined />
          )
        }
        aria-label={isOpen ? 'Expand less' : 'Expand more'}
        rounded
      />
    );

  return (
    <>
      <DallasFormField
        className={className}
        label={label}
        error={error}
        errorPlacement={errorPlacement}
        required={!!required}
        addOnStart={addOnStart}
        addOnEnd={addOnEnd}
        footnoteStart={footnoteStart}
        controlContainerRef={controlContainerRef}
      >
        <Select
          ref={ref}
          name={name}
          className={clsx(
            styles.select,
            addOnEnd == null ? styles.withoutAddOnEnd : styles.withAddOnEnd,
            addOnStart == null
              ? styles.withoutAddOnStart
              : styles.withAddOnStart,
            !value && formFieldStyles.placeholder,
          )}
          store={selectStore}
          onBlur={onBlur}
          aria-invalid={!!error}
          disabled={disabled}
        >
          {renderValue(value)}
        </Select>
        <DallasFlex />
      </DallasFormField>
      <DallasSelectPopover
        store={selectStore}
        gutter={8}
        getAnchorRect={() =>
          controlContainerRef.current?.getBoundingClientRect() || null
        }
        modal={withModalPopover}
        sameWidth
      >
        {options.map((option) => {
          const selected = option.value === value;

          return (
            <DallasSelectItem
              key={`${name}-option-${option.value}`}
              value={option.value}
              label={option.label}
              addOnEnd={
                selected ? (
                  <DallasSvgIconRadioButtonCheckedOutlined />
                ) : (
                  <DallasSvgIconRadioButtonUncheckedOutlined />
                )
              }
            />
          );
        })}
      </DallasSelectPopover>
    </>
  );
}
